import { DateText } from "@portal/BaseComponents/Date/DateText";
import { DetailsViewLayout } from "@portal/BaseComponents/DetailsView/DetailsView";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { AmountText } from "@portal/BaseComponents/Table/Components/Amount/AmountText";
import { InvoicesColumns } from "@portal/BaseComponents/Table/Components/Invoices/InvoicesColumn";
import { InvoicesText } from "@portal/BaseComponents/Table/Components/Invoices/InvoicesText";
import { mapInvoicesColumnPredicate } from "@portal/BaseComponents/Table/Components/Invoices/predicate";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import {
  getPaymentModeLabel,
  getPaymentStatus,
} from "@portal/Components/Portal/PotalTabs/Payment/predicate";
import { PaymentDetails } from "@portal/types/entities/payment";
import { StatusTag } from "@sinecycle/growcomponents";
import { DescriptionsProps } from "antd";
import SkeletonInput from "antd/es/skeleton/Input";
import { sum } from "lodash";
import { useMemo } from "react";

interface PaymentDetailsCompProps {
  data?: PaymentDetails;
  loading: boolean;
}
function PaymentDetailsComp(props: PaymentDetailsCompProps) {
  const { data, loading } = props;
  function getInvoices(): InvoicesColumns[] {
    if (!data) return [];
    return data.invoices.map(mapInvoicesColumnPredicate);
  }

  const invoices = useMemo(getInvoices, [data]);
  const Desc = loading ? (
    <SkeletonInput />
  ) : (
    <TypographyText>{data?.note ?? "-"}</TypographyText>
  );
  const Reference = loading ? (
    <SkeletonInput />
  ) : (
    <TypographyText strong>{data?.reference_no ?? "-"}</TypographyText>
  );
  const PaymentDate =
    !loading && data ? <DateText value={data.date} /> : <SkeletonInput />;
  const PaymentAmount =
    !loading && data ? (
      <AmountText
        amount={data.amount}
        currency={data.currency}
        format="FULL"
        textProps={{ strong: true }}
      />
    ) : (
      <SkeletonInput />
    );
  const PaymentMode = loading ? (
    <SkeletonInput />
  ) : (
    <TypographyText strong>
      {data ? getPaymentModeLabel(data?.mode)?.label : "-"}
    </TypographyText>
  );
  const PaymentStatus =
    data && !loading ? (
      <StatusTag
        text={getPaymentStatus(data?.status).label}
        style={getPaymentStatus(data?.status).style}
      />
    ) : (
      <SkeletonInput />
    );
  const AssociatedInvoice =
    data && !loading ? (
      <Flex vertical space="12">
        {invoices.length
          ? invoices.map((invoice) => {
              return (
                <Flex key={invoice.id} space="8">
                  <InvoicesText
                    overRideStyle={{ width: "auto" }}
                    invoice={invoice}
                    hide={{ amount: true }}
                    textStyle={{
                      title: {
                        fs: "14",
                        ellipsis: { tooltip: invoice.title },
                      },
                    }}
                  />
                  <AmountText
                    amount={sum(
                      data.invoice_payments[invoice.id].map((f) => f.amount)
                    )}
                    currency={data.currency}
                    format="FULL"
                    textProps={{ fs: "14" }}
                  />
                </Flex>
              );
            })
          : "-"}
      </Flex>
    ) : (
      <SkeletonInput />
    );
  const AppliedAmount =
    !loading && data ? (
      <AmountText
        amount={data.applied_amount}
        currency={data.currency}
        format="FULL"
        textProps={{ strong: true }}
      />
    ) : (
      <SkeletonInput />
    );
  const UnAppliedAmount =
    !loading && data ? (
      <AmountText
        amount={data.un_applied_amount}
        currency={data.currency}
        format="FULL"
        textProps={{ strong: true }}
      />
    ) : (
      <SkeletonInput />
    );

  function getItems(): DescriptionsProps["items"] {
    return [
      { key: "DESC", label: "Payment description", children: Desc, span: 2 },
      { key: "REF", label: " Payment Reference", children: Reference },
      { key: "DATE", label: "Payment Date", children: PaymentDate },
      { key: "AMOUNT", label: "Payment Amount", children: PaymentAmount },
      { key: "MODE", label: "Payment Mode", children: PaymentMode },
      {
        key: "INVOICE",
        label: "Associated Invoice",
        children: AssociatedInvoice,
      },
      { key: "STATUS", label: "Payment Status", children: PaymentStatus },

      { key: "APPLIED", label: "Applied Amount", children: AppliedAmount },
      {
        key: "UNAPPLIED",
        label: "UnApplied Amount",
        children: UnAppliedAmount,
      },
    ];
  }

  const items = getItems();
  return <DetailsViewLayout items={items} />;
}

export { PaymentDetailsComp };
